import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, VismaInvoiceSettings as VismaInvoiceSettingsType } from '@/types';
import { apiService } from '@/utils/api';

const VismaInvoiceSettings: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    article_coding_25: '',
    article_coding_12: '',
    article_coding_6: '',
    unit_id: ''
  });
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const queryClient = useQueryClient();

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const sitesData = await apiService.getSites();
        setSites(sitesData);
        if (sitesData.length > 0) {
          setSelectedSiteId(sitesData[0].id);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };
    fetchSites();
  }, []);

  // Fetch Visma invoice settings
  const { data: settingsData, isLoading } = useQuery<VismaInvoiceSettingsType>(
    ['visma-invoice-settings', selectedSiteId],
    () => apiService.getVismaInvoiceSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
      onSuccess: (data: VismaInvoiceSettingsType) => {
        setFormData({
          site_id: data.site_id?.toString() || '',
          article_coding_25: data.article_coding_25 || '',
          article_coding_12: data.article_coding_12 || '',
          article_coding_6: data.article_coding_6 || '',
          unit_id: data.unit_id || ''
        });
      },
      onError: () => {
        // Reset form if no data found
        setFormData({
          site_id: selectedSiteId?.toString() || '',
          article_coding_25: '',
          article_coding_12: '',
          article_coding_6: '',
          unit_id: ''
        });
      }
    }
  );

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: VismaInvoiceSettingsType) => {
      if (settingsData?.id) {
        return apiService.updateVismaInvoiceSettings(selectedSiteId!, data);
      } else {
        return apiService.createVismaInvoiceSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setShowSuccess(true);
        queryClient.invalidateQueries(['visma-invoice-settings', selectedSiteId]);
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteVismaInvoiceSettings(selectedSiteId!),
    {
      onSuccess: () => {
        setShowDeleteSuccess(true);
        setFormData({
          site_id: selectedSiteId?.toString() || '',
          article_coding_25: '',
          article_coding_12: '',
          article_coding_6: '',
          unit_id: ''
        });
        queryClient.invalidateQueries(['visma-invoice-settings', selectedSiteId]);
        setTimeout(() => setShowDeleteSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: VismaInvoiceSettingsType = {
      ...formData,
      site_id: parseInt(formData.site_id)
    };
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these invoice settings?')) {
      deleteMutation.mutate();
    }
  };

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSiteId = parseInt(e.target.value);
    setSelectedSiteId(newSiteId);
  };

  if (isLoading && selectedSiteId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Visma Invoice Settings</h1>

      {/* Success Messages */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Visma Invoice Settings saved successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {showDeleteSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Visma Invoice Settings deleted successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Something went wrong. Please try again.
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Site Selection */}
          <div>
            <label htmlFor="site_id" className="block text-sm font-medium text-gray-700 mb-2">
              Site
            </label>
            <select
              id="site_id"
              name="site_id"
              value={selectedSiteId || ''}
              onChange={handleSiteChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Article Coding 25% */}
            <div>
              <label htmlFor="article_coding_25" className="block text-sm font-medium text-gray-700 mb-2">
                Article Coding 25%
              </label>
              <input
                type="text"
                id="article_coding_25"
                name="article_coding_25"
                value={formData.article_coding_25}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* Article Coding 12% */}
            <div>
              <label htmlFor="article_coding_12" className="block text-sm font-medium text-gray-700 mb-2">
                Article Coding 12%
              </label>
              <input
                type="text"
                id="article_coding_12"
                name="article_coding_12"
                value={formData.article_coding_12}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* Article Coding 6% */}
            <div>
              <label htmlFor="article_coding_6" className="block text-sm font-medium text-gray-700 mb-2">
                Article Coding 6%
              </label>
              <input
                type="text"
                id="article_coding_6"
                name="article_coding_6"
                value={formData.article_coding_6}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* Unit ID */}
            <div>
              <label htmlFor="unit_id" className="block text-sm font-medium text-gray-700 mb-2">
                Unit ID
              </label>
              <input
                type="text"
                id="unit_id"
                name="unit_id"
                value={formData.unit_id}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>

            {settingsData?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default VismaInvoiceSettings;
